import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { createPlan } from "../../redux/slices/plansSlice";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';

const AddPlan = () => {
  const [formData, setFormData] = useState({
    name: "",
    detail: "",
    price: 0,
    referralLimit: 0,
    deviceLimit: 1,
    benefits: {
      streaming4K: false,
      offlineViewing: false,
      exclusiveContent: false
    },
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const validate = (data) => {
    const newErrors = {};
    if (!data.name.trim()) newErrors.name = "El nombre es obligatorio";
    if (!data.detail.trim()) newErrors.detail = "El detalle es obligatorio";
    if (data.price <= 0) newErrors.price = "El precio debe ser mayor a 0";
    if (data.referralLimit < 0) newErrors.referralLimit = "Debe ser 0 o mayor";
    if (data.deviceLimit < 0) newErrors.deviceLimit = "Debe ser 0 o mayor";
    
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedValue = type === "checkbox" ? checked : value;
    
    if (name.startsWith('benefit-')) {
      const benefitName = name.replace('benefit-', '');
      setFormData((prev) => ({
        ...prev,
        benefits: {
          ...prev.benefits,
          [benefitName]: checked
        }
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: updatedValue }));
    }
    
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate({ ...formData, [name]: updatedValue }));
  };



  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate(formData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(formData);
    setErrors(validationErrors);
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const res = await dispatch(createPlan(formData));
      if (res.meta.requestStatus === "fulfilled") {
        await Swal.fire({
        title: '¡Éxito!',
        text: 'Se ha creado el plan correctamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
      });
        navigate("/admin");
      } else {
        alert("Error al crear el plan");
      }
    } catch (error) {
      console.error(error);
      alert("Ocurrió un error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderError = (field) =>
    touched[field] && errors[field] && (
      <p className="text-red-500 text-sm mt-1">{errors[field]}</p>
    );

  return (
    <section className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center px-6 py-12">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-10 space-y-6 border border-white/20"
        noValidate
      >
        <h2 className="text-3xl font-bold text-center text-white">Crear Nuevo Plan</h2>

        {/* Nombre con opciones predefinidas y campo de texto */}
        <div>
          <label className="block text-sm font-medium text-white mb-1">Nombre:</label>
          <div className="relative">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              list="plan-names"
              placeholder="Selecciona o escribe un nombre"
              className="w-full px-4 py-3 rounded-xl bg-white/20 text-white border border-white/30 focus:outline-none focus:ring-2 focus:ring-cyan-400"
            />
            <datalist id="plan-names">
              <option value="Fan Épico" />
              <option value="Crítico Épico" />
              <option value="Productor Épico" />
            </datalist>
          </div>
          {renderError("name")}
        </div>
        
        {[
          { label: "Detalle", name: "detail", type: "textarea" },
          { label: "Precio", name: "price", type: "number" },
          { label: "Límite de Referidos", name: "referralLimit", type: "number" },
          { label: "Límite de Dispositivos", name: "deviceLimit", type: "number" },
        ].map(({ label, name, type }) => (
          <div key={name}>
            <label className="block text-sm font-medium text-white mb-1">{label}:</label>
            {type === "textarea" ? (
              <textarea
                name={name}
                value={formData[name]}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full px-4 py-3 bg-white/20 text-white border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-400 h-28 resize-none"
              />
            ) : (
              <input
                type={type}
                name={name}
                value={formData[name]}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full px-4 py-3 bg-white/20 text-white border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-400"
              />
            )}
            {renderError(name)}
          </div>
        ))}

        {/* Beneficios */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-white mb-1">Beneficios:</label>
          
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              name="benefit-streaming4K"
              checked={formData.benefits.streaming4K}
              onChange={handleChange}
              className="w-5 h-5 accent-cyan-500"
            />
            <label className="text-white font-medium">Streaming en 4K</label>
          </div>
          
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              name="benefit-offlineViewing"
              checked={formData.benefits.offlineViewing}
              onChange={handleChange}
              className="w-5 h-5 accent-cyan-500"
            />
            <label className="text-white font-medium">Visualización sin conexión</label>
          </div>
          
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              name="benefit-exclusiveContent"
              checked={formData.benefits.exclusiveContent}
              onChange={handleChange}
              className="w-5 h-5 accent-cyan-500"
            />
            <label className="text-white font-medium">Contenido exclusivo</label>
          </div>
        </div>

        {/* Botón */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-yellow-400 text-black hover:bg-yellow-500 disabled:bg-yellow-300 font-semibold py-3 rounded-xl shadow-md transition-all duration-300"
        >
          {isSubmitting ? "Creando plan..." : "Crear Plan"}
        </button>
      </form>
    </section>
  );
};

export default AddPlan;
